/**
 * A company's PROJECTS OF INTEREST — the projects a school is being pursued for.
 *
 * Owner's request, 2026-09-12: one column reading the projects a company is
 * being chased for, on the companies list, on the company record, and on the
 * University Partnerships dashboard.
 *
 * ## 🔴 THIS MODULE NAMES **TWO** FACTS SINCE 2026-09-14, AND ONLY ONE OF THEM
 *    IS A COMPANY'S
 *
 * A second heading lives here — {@link PROJECTS_ON_DOCUMENT_LABEL}, *"Projects
 * on this document"* — for the projects a DEAL, QUOTE or INVOICE's own line
 * items name. It is **not** a second spelling of the projects-of-interest
 * column; it is a different question about a different record, and the full
 * argument for why one module may name both without breaking its own
 * one-copy rule is at that constant and at
 * {@link PROJECTS_OF_INTEREST_LABEL}. ⛔ **Do not collapse them.**
 *
 * ⚠️ **The rendering helpers are SHARED between the two facts and the write
 * helpers are NOT.** {@link companyProjectsLabel} and
 * {@link companyProjectsTitle} decide how any list of projects reads in a dense
 * cell and are called by both; {@link projectOfInterestOptions} and everything
 * about the whole-set replace below belong to the company fact **alone**, because
 * a document's line projects are not writable from a cell at all — they are a
 * consequence of its line items. See the note on `companyProjectsLabel`.
 *
 * ## ⚠️ SEVEN RENDER SITES, ONE COPY — and ALL SEVEN WRITE
 *
 * 🔴 **THE THREE DOCUMENT ROWS LEFT THIS TABLE ON 2026-09-14 — owner's
 * decision.** *SUPERSEDED, the three rows below reading "yes / **yes**, through
 * the modal" for the Deals, Quotes and Invoices cards, and the paragraph after
 * the table that explained them.* Those cards now render
 * {@link PROJECTS_ON_DOCUMENT_LABEL} over the document's own line projects
 * instead, so they neither read nor write a company's projects of interest.
 *
 * ⚠️ **Kept rather than deleted, because the rows are the record of what a
 * document card USED to claim** — and because the paragraph under the table is
 * still the only statement in this repo of what the write's TARGET is, which is
 * what the two surviving dashboard cards still depend on.
 *
 * | where | the row is | reads | writes |
 * | --- | --- | --- | --- |
 * | `/companies` — the list column | a company | yes | **yes** |
 * | `/companies/:id` — the record | a company | yes | **yes** |
 * | `/university-partnerships` — the **Leads** card | a company | yes | **yes**, through the modal |
 * | `/university-partnerships` — the **Companies** card | a company | yes | **yes**, through the modal |
 * | ~~`/university-partnerships` — the **Deals** card~~ | ~~a **deal**~~ | ~~yes~~ | ~~**yes**, through the modal~~ |
 * | ~~`/university-partnerships` — the **Quotes** card~~ | ~~a **quote**~~ | ~~yes~~ | ~~**yes**, through the modal~~ |
 * | ~~`/university-partnerships` — the **Invoices** card~~ | ~~an **invoice**~~ | ~~yes~~ | ~~**yes**, through the modal~~ |
 *
 * ⚠️ **On Deals, Quotes and Invoices the row is a DOCUMENT and this column is
 * its COMPANY's.** The cell reads through to the company and the write is
 * `PATCH /companies/{id}` — never a write against the deal, quote or invoice,
 * none of which carries such a column or ever will. Two rows naming one company
 * therefore show one set and edit one record, so **a modal that refreshes only
 * the row it was opened from leaves every sibling row stating the old set**,
 * with a `200` and nothing failing. That is a fact about the write's target, not
 * about this module, which is why it is stated here rather than assumed.
 *
 * 🔴 **THAT PARAGRAPH IS THE REASON THE THREE ROWS WENT, WHICH IS WHY IT SURVIVES
 * THEM.** *"This column is its COMPANY's"* was a true, carefully-argued statement
 * about what the cell showed — and a document card is precisely where nobody
 * wanted the company's answer. **A correct explanation of a cell is not evidence
 * that the cell answers the question its card is about.**
 *
 * Seven copies would agree the day they were written and drift on the first
 * correction. The drift here is not cosmetic: six of the seven surfaces are
 * dense rows somebody is scanning to decide what to chase, so a cell that counts
 * differently, truncates differently or narrows its picker differently on one
 * screen than another is read as two different facts about one company. The same
 * argument as `src/lib/inclusions.ts`, `line-presets.ts` and
 * `document-naming.ts`, and it is why this file existed before the fourth copy
 * did — and why the fifth, sixth and seventh cost a table row rather than a
 * rewrite.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES. It decides.** The pages own their own
 * requests and their own draft state; this module answers *"given the projects
 * this company holds and the projects that may be offered, what should the
 * screen say?"* — which is the half that must not be reimplemented per page.
 * **There are no hooks here and none may be added**, and that ban is untouched
 * by the reversal below and independent of it: a hook would drag a fetch in
 * behind it, and the seven sites now need *different* fetches. ⚠️ *SUPERSEDED,
 * the reason as it was first written: "the two read-only dashboard cards already
 * have their rows."* **A modal genuinely does need its own options** — the
 * candidate projects and the cycles that scope them — where a cell needs only
 * the company's stored rows. A hook here would force one fetching shape on both
 * and make the cheap surface pay for the expensive one.
 *
 * ## ✅ THE UP DASHBOARD EDITS THIS NOW — REVERSED 2026-09-12, owner's instruction
 *
 * *SUPERSEDED, and it was argued at length: "**⛔ THE UP DASHBOARD RENDERS THIS
 * READ-ONLY.** Editing lives on `/companies` and `/companies/:id` and nowhere
 * else. The two dashboard cards call `companyProjectsLabel` and
 * `companyProjectsTitle` and must not call `projectOfInterestOptions` at all —
 * an options list on a card that cannot save one is a picker whose only outcome
 * is a discarded change."*
 *
 * The cell renders the project names and **clicking it opens a MODAL that adds
 * AND removes**, writing `PATCH /companies/{id}` exactly as `/companies` does.
 * So {@link projectOfInterestOptions} **is** called from the dashboard — by the
 * modal — on all five cards.
 *
 * ⚠️ **Quoted rather than struck through, deliberately: a prohibition that was
 * argued forcefully still reads as a prohibition after a line is drawn through
 * it**, and this one is the reason five surfaces were built with no picker. A
 * reader who meets only the new rule cannot tell which parts of the old one
 * survived — and two of them did.
 *
 * ### ✅ THE HALF THAT SURVIVES is the ARGUMENT, not the ruling
 *
 * **"Do not express it as a disabled control" still binds — TO THE CELL.** A
 * card is a report; a greyed picker on one reads as something the reader has not
 * earned the right to use, and that was never a claim about permissions. What
 * moved is *where the editor lives*, not *what the cell is*: **the cell is still
 * TEXT**, and the editor is a modal **launched from** it rather than a picker
 * embedded in a dense row. The old sentence's real complaint — an options list
 * inside a column somebody is scanning — is **answered** by the modal, not
 * overruled by it.
 *
 * ⛔ **So do not "finish" the reversal by inlining a `MultiCombobox` into a
 * dashboard cell.** Five cards on one page, every row carrying an open picker,
 * is the arrangement the old ruling and the new one both refuse. The modal is
 * what made the reversal possible; it is not a step on the way to the picker.
 *
 * ### ✅ AND THE CELL'S WORDING CHANGED WITH IT — owner's decision, same day
 *
 * *SUPERSEDED, an open question raised in this file and answered within the
 * hour: "**What did NOT change is `companyProjectsLabel`.** The cell still
 * renders the one project's NAME at 1 and a COUNT at 2 and above … If "shows the
 * project names" was meant as every name, always, in the cell, that is a change
 * to this module's behaviour and **it has not been made**."*
 *
 * It was meant that way. {@link companyProjectsLabel} **lists the names**, one
 * line, truncated, with `+N` for the remainder and the full set on
 * {@link companyProjectsTitle} — the owner's words were that the values should
 * be visible, and the count is what prompted the instruction. ⚠️ **Because this
 * is the one copy, `/companies` and `/companies/:id` changed too**; that is the
 * module's own rule working rather than a side effect, and the reasoning is at
 * the function so nobody restores the count on a screen that never asked.
 *
 * ## Projects of interest create and close NOTHING — owner's decision 2026-10-12
 *
 * A project of interest is a list on the company and nothing else. Adding one
 * creates no sales pursuit and removing one closes none: the sales tracker is a
 * view of DEALS now, and reads nothing from this list. The pursuit contract that
 * used to be documented here (add creates, remove closes as Lost) is retired, so
 * no copy, confirmation or tooltip anywhere may promise either side effect.
 *
 * Removal is expressed the only way it can be — by the caller sending a
 * **shorter set** — which is what the next section is about. Nothing here fetches
 * or writes, so there is no `removeProject` helper.
 *
 * ## The write is a whole-set REPLACE
 *
 * ```
 * absent    leaves the set alone
 * []        CLEARS every project of interest
 * [1, 7]    REPLACES the whole set
 * ```
 *
 * ⚠️ **One falsy value carrying two opposite instructions**, which is the same
 * rule as `cityIds`, `regionIds`, `inclusions`, `groupIds` and Xero's
 * `ContactPersons`, and the same trap: a form that hides this block must **omit
 * the key**, never send `[]`. The structural answer is the one
 * `changedGroupIds()` gives for user groups — build the value in exactly one
 * place, return `null` for *unchanged*, and let `[]` be reachable only by
 * somebody removing the last chip from a set that had one.
 *
 * ⛔ **A MODAL IS EXACTLY THE "HIDES THIS BLOCK" CASE THAT RULE NAMES, AND THE
 * DASHBOARD'S EDITOR IS A MODAL.** A modal that builds its `PATCH` from its own
 * STATE rather than from a DIFF — opened on a company, the picker never touched,
 * saved or closed through a path that still submits — sends `projectIds: []` and
 * **CLEARS the whole set**. There is no error to find: a `200`, an empty column
 * where a person only meant to look, and nothing anywhere recording that a set
 * existed. A collapsed section at least renders in the same tree as the control
 * that saves it; **a modal is unmounted the rest of the time, so "the block is
 * hidden" is its NORMAL state rather than an edge one.**
 *
 * ⚠️ **Which means "did the person change this?" cannot be answered by reading
 * the draft.** An untouched draft and a draft somebody emptied are the same
 * array. Diff against the set the modal OPENED with, emit the key only when they
 * differ, and treat *never opened the picker* as `undefined` — not as `[]`.
 *
 * ⚠️ **`[]` really does clear here**, unlike `productIds` on a project, where an
 * empty array means *sell only the primary* because one member is structural.
 * The two look identical in a payload and mean different things; do not carry
 * either reading across.
 *
 * ⚠️ **`[]` on the READ side is a different claim again.** This module renders
 * an empty set as *none*, because that is what an empty set means. A caller that
 * coalesces an unread or failed response into `[]` therefore turns *we do not
 * know* into *there are none*, on a column somebody reads to decide what to
 * chase. Never `?? []` at the boundary — pass what the server sent.
 *
 * ## ⚠️ THREE THINGS THE DASHBOARD MODAL'S AUTHOR NEEDS, each silent when missed
 *
 * Written out because the reversal above created a **new** caller of
 * {@link projectOfInterestOptions} that has no neighbouring call site to copy
 * from on its own page, and every one of these fails with a `200`.
 *
 * 1. 🔴 **`scopes` IS OPTIONAL, AND OMITTING IT IS NO NARROWING — NOT THE
 *    OWNER'S OPEN-CYCLES NARROWING.** The default is `UNKNOWN_CYCLE_SCOPES`,
 *    which `projectInCycleScope` reads as OFFER EVERYTHING, deliberately and in
 *    the safe direction. The two are indistinguishable on any screen where every
 *    project happens to be current, so an unnarrowed picker is merely twice as
 *    long rather than visibly wrong. **Pass `cycleScopeIdIndex(cycles)`**, and
 *    read the absence of that argument as **work outstanding rather than as a
 *    decision** — the full argument is on the function itself.
 *
 * 2. 🔴 **PIN THE STORED PROJECTS BY GOING THROUGH THIS FUNCTION. The injection
 *    is load-bearing BECAUSE the write is a whole-set replace.** A stored project
 *    the options omit renders as a chip labelled with the **raw id**, and the
 *    array the modal assembles is the array the company ends up with — so that
 *    project is **DROPPED on the next save, with a `200`** and nothing on screen
 *    having said so. Assembling options by hand from the fetched list, or
 *    narrowing the *fetch* by cycle instead of narrowing here, is exactly how it
 *    gets lost: narrowing the fetch also makes an out-of-scope stored project
 *    indistinguishable from a retired one, which is the distinction
 *    {@link projectOfInterestOptions} exists to keep.
 *
 * 3. ⛔ **RENDER THE `hasMore` FOOTNOTE.** `/projects` caps `perPage` at **100**
 *    and there are **113 ACTIVE PROJECTS** (measured 2026-09-12), so `hasMore`
 *    is **TRUE on every open** and roughly **13 projects are unreachable** — the
 *    same tail of the alphabet, every time, which reads as a naming convention
 *    rather than as a cap. ✅ **ALL THREE SURFACES RENDER IT** — `/companies`,
 *    `/companies/:id` (since 2026-09-13) and `CompanyProjectsModal`.
 *
 *    ⚠️ *SUPERSEDED 2026-09-14: "`/companies` renders that footnote;
 *    **`/companies/:id` does NOT, and is today the one place somebody can fail
 *    to find a project and not be told why.** The modal must not become the
 *    second."* **It was closed the day after that was written, and the sentence
 *    outlived it by a day inside the module that is this column's single
 *    decision point** — so a reader came here to check, found the gap named in
 *    the most authoritative place available, and was sent to redo finished work.
 *    A claim that some OTHER surface is missing something is the shape that rots
 *    without anything failing: nothing breaks when the gap closes.
 *
 * 3b. 🔴 **AND THE FOOTNOTE COSTS THE `Select all` ROW ON THIS COLUMN —
 *    OWNER'S DECISION 2026-09-14, AND IT REVERSES ONE OF THEIRS.**
 *    `MultiCombobox` drops Select all whenever a `footnote` is present unless
 *    the caller passes `'even-when-truncated'`, and `InlineRegionsField` has no
 *    such prop — so a caller **cannot** pass it. Truncation fires on essentially
 *    every open, so the row is effectively never rendered here.
 *
 *    ⚠️ *SUPERSEDED: the ruling that this picker HAS Select all "on the owner's
 *    instruction reaffirmed after the cost was put to them", with "⛔ **Do not
 *    'restore' the omission**" beside it.* **Put to the owner again on
 *    2026-09-14, with the conflict named, and they accepted the loss.** The
 *    footnote wins.
 *
 *    The reason: ticking all of a list the server cut short claims ~13 projects
 *    it never showed, and saves that set to the company. (The pursuit cost once
 *    cited here is gone — owner's decision 2026-10-12, projects of interest
 *    create and close nothing — but a saved set claiming rows never shown is
 *    still wrong, so the interlock stands.)
 *
 *    ⛔ **`'even-when-truncated'` IS NOT THE ESCAPE.** It is documented
 *    filters-only and says in terms not to pass it on an edit field: a filter's
 *    selection is evaluated and discarded, this one writes the company's set.
 *    ⚠️ **The only route to having both is PAGING the options in**, which
 *    nobody has asked for — not a bigger `perPage`, which has no larger value
 *    to reach for.
 *    - ⚠️ **This is a FACT ABOUT THE DATA, which is the documented exception to
 *      the no-helper-text rule** — the same standing as a `Combobox`'s
 *      truncation footnote (*"Showing the first 200 of 400"*), which reports what
 *      the reader is looking at rather than advising them what to do. It is not a
 *      hint and a helper-text sweep must not take it.
 *    - ⛔ **The fix is never a bigger `perPage`**: 100 is the server's own
 *      ceiling and there is no larger value to reach for. Paging, or the
 *      footnote. Nothing here may hardcode either number — count the response.
 */

import type { MultiComboboxOption } from '@/components/ui/MultiCombobox';
// `pluralise` was dropped here on 2026-09-12 with the count it served; see
// `companyProjectsLabel`. `PROJECTS_OF_INTEREST_NOUN` survives it and is still
// spelled out in both forms, for the pickers' `summaryNoun`.
import { orDash } from './format';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  UNKNOWN_CYCLE_SCOPES,
  projectInCycleScope,
  type CycleScopeIdIndex,
  type ProjectCycleScope,
} from './project-cycle-scope';

/**
 * The heading for a COMPANY's projects of interest, spelled once.
 *
 * ⚠️ **Singular, and that is the owner's word rather than a slip.** It names the
 * COLUMN — the question each cell answers — where the cell's own summary counts
 * what it found ({@link PROJECTS_OF_INTEREST_NOUN}). Seven headings written by
 * hand is how one screen ends up saying "Projects" and another "Pursuing" — and
 * the modal's own title is an eighth place the word has to come from.
 *
 * 🔴 **IT IS NO LONGER THE ONLY HEADING THIS MODULE OWNS — 2026-09-14, owner's
 * decision.** {@link PROJECTS_ON_DOCUMENT_LABEL} is the second, and the two must
 * be read together: the one-heading rule above was **SPENT, not relaxed**, on
 * the one thing it was always going to have to be spent on — a SECOND FACT. It
 * still forbids a ninth hand-written spelling of *this* one.
 *
 * ⛔ **SO THIS CONSTANT'S REACH NARROWED ON THE SAME DAY, and nothing about its
 * wording changed to say so.** It headed a column on all five University
 * Partnerships cards; it now heads one on the two COMPANY cards, because the
 * three document cards answer a different question. A reader who finds it
 * imported by a document surface has found a bug, not a shared decision.
 */
export const PROJECTS_OF_INTEREST_LABEL = 'Project of interest';

/**
 * The heading for the projects a DOCUMENT'S OWN LINE ITEMS name — the Deals,
 * Quotes and Invoices cards on the University Partnerships dashboard.
 *
 * ## 🔴 TWO FACTS, SO TWO LABELS — owner's decision, 2026-09-14
 *
 * Those three cards used to render {@link PROJECTS_OF_INTEREST_LABEL} over the
 * row's **ACCOUNT's** projects of interest, reached through the `company` embed. That was
 * correct about what it showed and was the wrong question to ask on a document
 * row: *what is this account being chased for* is not *what does this deal
 * SELL*, and the two routinely disagree. The three cards now show the document's
 * own line projects, and **Leads, Companies, `/companies` and `/companies/:id`
 * are UNCHANGED** — those rows genuinely are companies, so the account's list is
 * the only list they have.
 *
 * ## ⚠️ WHY THIS MODULE NOW NAMES TWO THINGS — record it, do not "fix" it
 *
 * The rule at {@link PROJECTS_OF_INTEREST_LABEL} — *seven headings written by
 * hand is how one screen ends up saying "Projects" and another "Pursuing"* — is
 * about **one fact spelled several ways**. It has never been an argument that
 * this module may only own one string, and collapsing these two back into one
 * would not be obeying it: it would be putting one word over two different
 * questions, which is the failure the rule exists to prevent, arriving from the
 * other direction.
 *
 * ⛔ **So the next reader must not tidy this back to a single constant**, and
 * must not derive one from the other. They are a pair only in the sense that
 * both name a list of projects; they name it **about different records**.
 *
 * ## ⚠️ PLURAL, where its neighbour is SINGULAR — the owner's wording, kept
 *
 * *"Projects on this document"* names a **SET carried by one record**;
 * *"Project of interest"* names the **QUESTION a column asks about an account**.
 * ⛔ **Do not harmonise them in either direction.** Making this one singular
 * would describe a cell that routinely holds eight; making the other plural would
 * overturn a wording the owner chose and that
 * {@link PROJECTS_OF_INTEREST_LABEL} already defends in its own doc.
 *
 * ## ⛔ THE TWO HEADINGS ARE INTERCHANGEABLE IN CODE AND NOT IN MEANING
 *
 * Both cells render through {@link companyProjectsLabel} and both hold
 * `{id, name}[]`, so **swapping the headings COMPILES and prints a perfectly
 * plausible list under the wrong question** — a confident wrong answer rather
 * than a blank, which is the one failure mode nobody reports. Nothing
 * typechecks a heading; this paragraph is the whole guard.
 *
 * ⚠️ **"on this document" is load-bearing and a bare "Projects" is not an
 * abbreviation of it.** The word that does the work is the one saying WHOSE
 * projects these are — on a card whose neighbouring cells are all about the
 * account (its country, its source, its open task), a heading reading "Projects"
 * would be read as the account's, which is exactly the reading this change
 * removes.
 */
export const PROJECTS_ON_DOCUMENT_LABEL = 'Projects on this document';

/**
 * The noun a COUNT reads with — **both forms spelled out, never derived**.
 *
 * ⚠️ **ONE consumer now, not two — corrected 2026-09-12.** *SUPERSEDED: "**This
 * is exported because it has TWO consumers that must agree** … the summary
 * string this module builds, and the `summaryNoun` prop the editing surfaces
 * hand `MultiCombobox` … The table cell and the picker sitting in that same cell
 * saying "2 projects" and "2 selected" is precisely the drift this file exists
 * to prevent."* **{@link companyProjectsLabel} no longer builds a summary
 * string** — it lists names — so the `summaryNoun` prop is the only consumer
 * left.
 *
 * ⚠️ **The cell and a picker trigger now genuinely say different things, and
 * that is not the drift the old text feared.** A cell reporting `BSF India,
 * Campus Tours +2` beside a collapsed picker reading `4 projects` is two
 * surfaces doing two jobs — a report of VALUES, and a count of SELECTIONS inside
 * an editor. The drift worth preventing was ever two *cells* disagreeing. Do not
 * reconcile them by putting the count back.
 *
 * **It is kept, rather than inlined, for the rule below**: a picker's collapsed
 * trigger is still a count, and it must not learn to build its plural itself.
 *
 * Pass it straight through:
 *
 * ```tsx
 * <MultiCombobox maxVisibleChips={0} summaryNoun={PROJECTS_OF_INTEREST_NOUN} … />
 * ```
 *
 * ⚠️ **Nothing pluralises algorithmically here, and nothing may.** `project` →
 * `projects` is regular and `format.ts`'s `pluralise` would default the plural
 * correctly — which is exactly why the habit survives long enough to reach the
 * first irregular noun. `src/lib/inclusions.ts` states the rule and what it
 * costs when it is broken (`1 campuses`, on a document a customer receives);
 * both forms are written out so this one is never the exception that taught
 * somebody the shortcut works.
 */
export const PROJECTS_OF_INTEREST_NOUN = { singular: 'project', plural: 'projects' };

/**
 * A project as this column reads it: an id and a name.
 *
 * ⚠️ **Structural on purpose, and deliberately NOT an import from `types.ts`.**
 * Three things satisfy it — the `MasterRef`-shaped rows the company read
 * carries, a `Project` off `/projects`, and the `{id, name}` a picker already
 * holds — and every render site is handed a slightly different one of them.
 * Naming a wire type here would make this module wrong the moment the response
 * shape moved, in a file that has no business knowing about the wire at all.
 * `project-cycle-scope.ts` takes the same approach for the same reason.
 */
export interface CompanyProject {
  id: number;
  name: string;
}

/** A blank name would render an unselectable-looking row; the id still identifies it. */
function labelFor(id: number, name: string): string {
  const trimmed = name.trim();
  return trimmed === '' ? `#${id}` : trimmed;
}

/**
 * ONE project's name, as anything rendering a project should spell it.
 *
 * The same `#id` fallback {@link companyProjectsLabel} and
 * {@link companyProjectsTitle} already apply, exposed for a caller that renders
 * projects ONE PER ROW rather than joined into a cell — today
 * `DocumentProjectsModal`, which lists a document's line projects in full.
 *
 * ⚠️ **It exists so that caller cannot re-spell the fallback**, which is a
 * three-token expression anybody would reproduce from memory and get subtly
 * wrong: a name that is whitespace-only is not blank to `=== ''`, and an
 * untrimmed name renders a row that looks indented. Two copies would agree on
 * every project in production today — every one has a name — and diverge on
 * exactly the row that motivated the fallback.
 *
 * ⚠️ **It takes the project, where {@link labelFor} takes the two fields.** The
 * internal callers already hold them apart inside a `map`; an external caller
 * holds the object, and unpacking it at the call site is the shape that lets
 * somebody pass the wrong id with the right name.
 */
export function projectDisplayName(project: CompanyProject): string {
  return labelFor(project.id, project.name);
}

/**
 * How many names {@link companyProjectsLabel} spells out before it falls back to
 * `+N`. **Not a taste setting — it is derived from the narrowest column this
 * renders in**, and the derivation is written out on that function. Moving it
 * means re-measuring, not re-guessing, and moving the 12rem column means
 * revisiting this.
 *
 * 🔴 **IT IS A FUNCTION OF THE COLUMN AND NOT OF THE DATA, WHICH IS WHY A SECOND,
 * MUCH LONGER-TAILED POPULATION DID NOT MOVE IT — 2026-09-14.** The document
 * cards' line projects render in the SAME 12rem slot on the SAME cards, so the
 * budget the derivation is built on is unchanged and the cap is still *the
 * largest whose best case fits it*. ⛔ **Do not re-cut it from the new
 * distribution**: a cap chosen so that the typical row fits would have to be 0,
 * because the typical row does not fit at any cap — that is what CSS `truncate`
 * and {@link companyProjectsTitle} are for.
 *
 * ⚠️ **What the second population DID change is how often the shortfall
 * happens** — measured: ~21% of quotes and ~12% of invoices name three or more
 * line projects, against a maximum of 8, where a company's own set maxes out at
 * 3. So `+N` fires several times per card rather than occasionally, and the
 * `title` stops being a recovery path and becomes the only complete reading. See
 * {@link companyProjectsTitle}.
 */
const NAMES_SHOWN = 2;

/**
 * What a dense cell says: **the project NAMES**, with a marker for any it could
 * not fit.
 *
 * ```
 * []                                  ->  '—'
 * [Bocconi Autumn 2026]               ->  'Bocconi Autumn 2026'
 * [BSF India, Campus Tours]           ->  'BSF India, Campus Tours'
 * [those two, and two more]           ->  'BSF India, Campus Tours +2'
 * ```
 *
 * ## 🔴 THE COUNT IS GONE — owner's decision, 2026-09-12
 *
 * *SUPERSEDED: "**What a dense cell says: the one project's name, or a count.**
 * … `[two of them] -> '2 projects'`. **The count at two and above is the
 * established answer to this exact problem, not a shortcut** … the line grids'
 * city multiselect reads `4 cities` on one line."*
 *
 * Put to the owner as a decision and chosen explicitly: **the values must be
 * visible.** `2 projects` is precisely the rendering that prompted the
 * instruction — it answers *how many* when the reader opened the column to learn
 * *which*, and a count is the one thing they could already infer from the row
 * being non-empty. Names on one line, truncated, with the full list on hover.
 *
 * ⚠️ **The `4 cities` precedent is not overturned, it is DISTINGUISHED.** That
 * collapse lives inside a line-item grid where the cell also holds a *picker*,
 * so four chips wrap to four rows and take the grid row with them. Here the cell
 * is TEXT — the editor is a modal launched from it — so one truncated line costs
 * one line whatever it contains. Do not carry this ruling back to the city
 * multiselect.
 *
 * ## ⚠️ THIS CHANGED `/companies` AND `/companies/:id` TOO, DELIBERATELY
 *
 * The instruction came from the dashboard, and **this is the one copy**, so the
 * company list column and the company record stopped reading `2 projects` and
 * started reading the names on the same day. That is **the module's own rule
 * being obeyed, not a side effect to apologise for**: the header argues that a
 * cell which counts differently on one screen than another is read as two
 * different facts about one company, and a second `companyProjectsNames()` for
 * the dashboard alone would have been exactly that drift. It was proposed and
 * **refused**.
 *
 * ⛔ **So do not "restore" the count on `/companies` on the view that the
 * dashboard diverged.** Nothing diverged; seven surfaces moved together, which
 * is the whole reason they share a function.
 *
 * ## How {@link NAMES_SHOWN} was chosen
 *
 * The narrowest column this renders in is **12rem** (the dashboard cards' own
 * width for it). Less `TD`'s `px-3` on both sides — 24px — leaves **~168px** of
 * content, and at `--text-sm` (13px) a mixed-case proportional glyph averages
 * ~0.5em, so the line holds **roughly 25–26 characters**.
 *
 * Two is the **largest cap whose best case still fits that budget**: two short
 * names plus the marker (`Campus Tours, Meet Up +1`) is 23 characters and fits,
 * where three names cannot at any realistic length. Two *typical* names do not
 * fit either — live project names run 19–32 characters apiece — and that is
 * accepted rather than designed around: **the cap bounds the STRING, and CSS
 * `truncate` guarantees the FIT.** Lowering it to 1 would hide a second name
 * that often would have fitted, which is the thing the owner asked to see.
 *
 * ⚠️ **The marker sits at the TAIL, so an ellipsis eats it first.** It is a
 * bonus signal, not the primary one — the ellipsis itself says *there is more*,
 * and {@link companyProjectsTitle} is the recovery. ⛔ **Do not "fix" that by
 * moving the marker to the front**: a leading `+2` reads as a count, which is
 * the rendering this change exists to remove.
 *
 * ⚠️ **An empty set renders as *none*, so do not hand it one you do not have.**
 * `'—'` is a claim that this company is being pursued for nothing. A list that
 * failed to load, or has not arrived, is a different fact and belongs in the
 * caller's own loading and error states — see the header on `?? []`. **That
 * distinction is untouched by this change** and is the one part of the old
 * rendering that had nothing to do with the count.
 *
 * ## ✅ REUSED BY THE DOCUMENT CARDS — decided 2026-09-14, not inherited
 *
 * The Deals, Quotes and Invoices cards render a DIFFERENT FACT through this same
 * function: the projects their own line items name
 * ({@link PROJECTS_ON_DOCUMENT_LABEL}). **A second `documentProjectsLabel` was
 * considered and refused**, and the reason is the one this module is built on:
 * the two would be byte-identical the day they were written, and what would
 * drift is *how a list of projects reads in a dense cell* — the separator, the
 * cap, the marker, the empty glyph — on cards that sit side by side on one page.
 * A reader comparing a quote row with a company row would be reading two
 * renderings of the same shape and would take the difference for a difference in
 * the data.
 *
 * ⛔ **What is NOT shared is the meaning, and that is carried by the HEADING, not
 * by this function.** This function words a list; it has never known whose list
 * it is, which is precisely what makes it safe to share and what makes
 * {@link PROJECTS_ON_DOCUMENT_LABEL} load-bearing. ⛔ **Do not add a parameter to
 * teach it the difference** — the moment it words one fact differently from the
 * other, sharing it has bought nothing and the two cells drift inside one
 * function instead of across two.
 *
 * ⚠️ **THE NAME SAYS `company` AND THE FUNCTION IS NOT COMPANY-SPECIFIC.** It was
 * named for its first caller. Renaming it is a sweep across four files this
 * session does not own, so it is recorded as a deliberate non-change rather than
 * done by halves — ⛔ and **an alias must not be added**, because two names for
 * one function is the drift this module exists to prevent, arriving as a
 * convenience.
 *
 * 🔴 **THE CAP AT {@link NAMES_SHOWN} WAS DERIVED FOR THE COMPANY POPULATION AND
 * THE DOCUMENT ONE IS LONGER-TAILED.** Measured 2026-09-14: a company's set maxes
 * out at **3**, where **~21% of quotes and ~12% of invoices** name three or more
 * line projects and the maximum is **8**. The cap is still right — it is a
 * function of the 12rem column, which both cells share — but `+N` now fires
 * several times per card, and **the marker sits at the TAIL where an ellipsis
 * eats it first**. So on these three cards {@link companyProjectsTitle} is not a
 * nicety: it is the only place the full set is readable, and a caller rendering
 * this label without it has built a cell that silently omits projects.
 */
/**
 * The same set as {@link companyProjectsLabel}, as PILL ITEMS showing every
 * project — owner's decision 2026-09-25: list cells render all values as
 * wrapping pills, so the two-name cap and its `+N` no longer apply to a cell.
 * The label keeps its callers (filter options, sentences). Same `#id`
 * fallback for a blank name, so the two cannot disagree about one project.
 */
export function companyProjectsPills(
  projects: readonly CompanyProject[],
): { key: number; label: string }[] {
  return projects.map((p) => ({ key: p.id, label: labelFor(p.id, p.name) }));
}

export function companyProjectsLabel(projects: readonly CompanyProject[]): string {
  // `orDash(null)` rather than a literal em dash: the glyph this app renders
  // for an absent value lives in `format.ts` and has exactly one copy.
  if (projects.length === 0) return orDash(null);

  const shown = projects.slice(0, NAMES_SHOWN).map((p) => labelFor(p.id, p.name)).join(', ');
  const hidden = projects.length - NAMES_SHOWN;
  /*
   * No marker at exactly the cap: `+0` is a promise of more with nothing behind
   * it, and at 1 and 2 projects the line is already the whole truth.
   *
   * A bare `+N` rather than `+N more` or `+N projects`: the cell is 168px and
   * every character spent on a word is a character taken off a name, which is
   * what the reader came for. The noun is in the column heading above it.
   */
  return hidden > 0 ? `${shown} +${hidden}` : shown;
}

/**
 * The whole list, for the cell's `title` — or `undefined` when there is nothing
 * to say.
 *
 * The established pattern on `/projects`: the chip carries the bare city name
 * and the cell's `title` carries the country, so the column stays narrow and the
 * detail is one hover away rather than gone.
 *
 * 🔴 **THIS IS LOAD-BEARING SINCE 2026-09-12, NOT A NICETY — and the function
 * did not change, its job did.** *SUPERSEDED, the sentence above it: "the
 * visible text collapses to a count and the `title` is where the names
 * survive."* The label now lists names and **truncates** — by the cap at
 * {@link NAMES_SHOWN}, and again by CSS at whatever the column is — so this is
 * the ONLY place the full set is readable, and the `+N` marker that announces
 * the shortfall is itself the first thing an ellipsis removes. **A caller that
 * renders the label without this `title` has built a cell that silently omits
 * projects.**
 *
 * 🔴 **AND IT GOT MORE LOAD-BEARING AGAIN ON 2026-09-14, FOR A REASON THAT IS
 * ABOUT THE DATA RATHER THAN THE CODE.** The Deals, Quotes and Invoices cards now
 * render a document's own LINE projects through the same helpers, and that
 * population is far longer-tailed: measured, **~21% of quotes and ~12% of
 * invoices name three or more, against a maximum of 8**, where a company's set
 * maxes out at **3**. So on those three cards the `+N` marker is the ordinary
 * case rather than the exception, and this `title` is the only complete reading
 * of the cell for roughly one row in five.
 *
 * ⚠️ **Nothing in this function changed and nothing needed to — its JOB got
 * bigger a second time.** That is worth stating because a helper whose behaviour
 * is unmoved is exactly the one nobody re-reads when the data under it moves:
 * there is no diff, no failing build and no wrong render, only a recovery path
 * carrying more weight than the last person to look at it assumed.
 *
 * ⚠️ **`undefined` rather than `''` on an empty set.** React omits the attribute
 * entirely for `undefined`, where `title=""` is a real, empty tooltip on some
 * browsers — and the cell already says `'—'`, which needs no elaboration.
 *
 * **It is returned at exactly 1 as well, where it duplicates the visible text,
 * and that is deliberate**: a single project name is the case most likely to be
 * long enough to truncate, and it is the one case where the `title` is the only
 * way to read the rest of it. That argument now reaches **every** non-empty set
 * rather than just the singleton, which is why nothing here gained a length
 * test.
 *
 * ⚠️ **A plain `', '` join and nothing cleverer.** This is a list, not a
 * sentence, so `joinList`'s trailing "and" is wrong — and it must stay stable,
 * because a `title` is the recovery path for text the cell has already cut off.
 */
export function companyProjectsTitle(
  projects: readonly CompanyProject[],
): string | undefined {
  if (projects.length === 0) return undefined;
  return projects.map((project) => labelFor(project.id, project.name)).join(', ');
}

/** What {@link projectOfInterestOptions} needs of a candidate project. */
export interface SelectableProject extends CompanyProject {
  /** Nullable only because the join is `LEFT`; `cycle_id` is `NOT NULL`. */
  cycle: { id: number } | null;
}

/**
 * ⛔ **NOTHING TAKES THIS ANY MORE — {@link projectOfInterestOptions} became
 * POSITIONAL on 2026-09-12.** It is kept only because its per-field notes are
 * the documentation for that function's four arguments, and deleting it would
 * take them with it. **Read it as a description of the parameters, not as a
 * shape anybody passes** — an interface that still compiles and names no live
 * call site is exactly the artefact a grep finds and believes.
 */
export interface ProjectOfInterestOptionsInput {
  /**
   * Every project the picker could offer, as fetched — **unfiltered by cycle**.
   * The scope narrowing happens here so that {@link stored} can be resolved
   * against the full list; narrowing the fetch instead is what makes an
   * out-of-scope stored project indistinguishable from a retired one.
   */
  projects: readonly SelectableProject[];
  /**
   * Both cycle scopes' id sets, from `cycleScopeIdIndex(cycles)`.
   *
   * ⚠️ A `null` member means *the cycles could not be read*, **not an empty
   * set**, and {@link projectInCycleScope} reads it as OFFER EVERYTHING.
   * `UNKNOWN_CYCLE_SCOPES` is the shape to pass while `/cycles` is unread.
   */
  scopes: CycleScopeIdIndex;
  /** The projects of interest the company already holds. */
  stored: readonly CompanyProject[];
  /** Which half of the cycle calendar to offer. Defaults to open cycles. */
  scope?: ProjectCycleScope;
}

/**
 * The picker's options: the projects in scope, plus **every** stored project
 * that is not among them, pinned at the top.
 *
 * 🔴 **THE INJECTION IS THE LOAD-BEARING HALF AND IT IS WHY THIS IS SHARED.**
 * The write is a whole-set REPLACE, so the array the editor assembles is the
 * array the company ends up with — and `MultiCombobox` renders a value with no
 * matching option as a chip **labelled with the raw value**, an id. So a stored
 * project the options omit is either an id sitting where a name should be, or,
 * in the count-only cell, a number with nothing behind it. Either way the next
 * save of an unrelated field carries whatever the chips resolved to, and a
 * project of interest is dropped with a `200` and nothing on screen having said
 * so. This is `withStoredOption`'s rule, and `groupMembershipOptions`', arriving
 * on a third set-valued field.
 *
 * ⚠️ **Narrowing by cycle narrows what may be CHOSEN and never what may be
 * DISPLAYED or SAVED.** The server has no opinion whatever about a project's
 * cycle, so a company already interested in a project whose cycle closed last
 * year is a perfectly valid row and must keep rendering as itself. That is
 * inherited verbatim from `project-cycle-scope.ts`, which states it at length.
 *
 * ## Two reasons a stored project is missing, and they are NOT the same fact
 *
 * | it is | so the hint reads |
 * | --- | --- |
 * | in `projects`, but under the other cycle scope | that scope's own label — `Previous` |
 * | absent from `projects`, which is non-empty | `No longer offered` |
 * | absent from `projects`, which is EMPTY | **nothing** — see below |
 *
 * ⚠️ **Conflating the first two would accuse a live project of being retired**
 * purely because this app narrowed the list itself. The wording for the first is
 * taken from `PROJECT_CYCLE_SCOPE_LABELS` rather than written again, so one word
 * means one thing whether it labels a pill or explains a pin.
 *
 * ⚠️ **An EMPTY `projects` gets no hint at all**, exactly as `withStoredOption`
 * does: empty means *still loading* or *failed*, never *every project was
 * retired*, and absence proves nothing about a value we cannot cross-reference.
 * The stored project still appears — with its name — so the picker shows what
 * the company actually holds instead of going blank for a frame or two.
 */
/**
 * ⚠️ **POSITIONAL, AND THE ORDER IS `(projects, stored)` — 2026-09-12.**
 * *SUPERSEDED: a single `ProjectOfInterestOptionsInput` object.* Three render
 * sites were written against the positional form independently and in parallel
 * — `CompaniesPage`, `CompanyDetailPage` and the company picker — each mirroring
 * the two neighbours it sits beside, `projectOptions(projects, stored)` and
 * `regionPickerOptions(records, stored)`. Three call sites agreeing with the
 * surrounding convention beat one signature agreeing with itself, so the module
 * moved rather than the callers.
 *
 * 🔴 **`scopes` IS OPTIONAL, AND OMITTING IT OFFERS EVERY PROJECT.** That is the
 * safe direction and it is deliberate: `projectInCycleScope` already reads a
 * `null` member as OFFER EVERYTHING, because a cycle list that has not loaded is
 * not evidence that a project is out of scope. An over-offered project is a row
 * somebody can see and decline; an under-offered one is a project that cannot be
 * chosen at all, with nothing on screen saying why.
 *
 * ⛔ **So a caller that omits `scopes` has NOT got the owner's open-cycles
 * narrowing — it has got no narrowing.** The two look identical on a screen
 * where every project happens to be current, which is most of them today: 57 of
 * the 113 active projects sit in open cycles (measured 2026-09-12), so an
 * unnarrowed picker is merely twice as long rather than visibly wrong. **Pass
 * `cycleScopeIdIndex(cycles)` to get the behaviour that was actually asked
 * for**, and read the absence of that argument as work outstanding rather than
 * as a decision.
 *
 * ⚠️ **THE UP DASHBOARD'S MODAL IS A CALLER OF THIS SINCE 2026-09-12** — the
 * reversal recorded in the header — and it is the first one with no neighbouring
 * call site on its own page to copy the argument list from. Both of the above
 * are therefore easiest to get wrong there, and both are silent: an unnarrowed
 * picker looks like a long picker, and an unpinned stored project looks like a
 * chip. ⛔ **It also owes the reader the `hasMore` footnote** — `/projects` caps
 * `perPage` at 100 against 113 active projects, so the candidate list handed to
 * this function is ALWAYS short and this function cannot know that. See the
 * header.
 */
export function projectOfInterestOptions(
  projects: readonly SelectableProject[],
  stored: readonly CompanyProject[],
  scopes: CycleScopeIdIndex = UNKNOWN_CYCLE_SCOPES,
  scope: ProjectCycleScope = DEFAULT_PROJECT_CYCLE_SCOPE,
): MultiComboboxOption[] {
  const inScope: MultiComboboxOption[] = projects
    .filter((project) => projectInCycleScope(project, scopes[scope]))
    .map((project) => ({
      value: String(project.id),
      label: labelFor(project.id, project.name),
    }));

  const listed = new Set(inScope.map((option) => option.value));
  const byId = new Map(projects.map((project) => [project.id, project]));

  const pinned: MultiComboboxOption[] = [];
  for (const project of stored) {
    const value = String(project.id);
    // A repeated id would pin the same project twice. `(company_id,
    // project_id)` is UNIQUE so it should not arrive, but a duplicated chip is
    // the kind of thing a reader reports as the control double-saving.
    if (listed.has(value)) continue;
    listed.add(value);

    const hint = pinnedHint(byId.get(project.id), projects.length, scopes, scope);
    pinned.push({
      value,
      label: labelFor(project.id, project.name),
      ...(hint === null ? {} : { hint }),
    });
  }

  return [...pinned, ...inScope];
}

/**
 * Why a stored project is pinned rather than listed, or `null` when we cannot
 * honestly say.
 *
 * The other-scope test reads the id set **directly** rather than through
 * `projectInCycleScope`, which resolves an unreadable set and a cycle-less
 * project as offerable. Both of those would have passed the chosen scope too —
 * so reaching here means neither applies — but going through the lenient helper
 * would make this label depend on a fallback that cannot fire, which is one edit
 * away from claiming a scope on the strength of a `null`.
 */
function pinnedHint(
  project: SelectableProject | undefined,
  candidateCount: number,
  scopes: CycleScopeIdIndex,
  scope: ProjectCycleScope,
): string | null {
  if (project !== undefined) {
    const cycleId = project.cycle?.id;
    if (cycleId !== undefined) {
      for (const other of PROJECT_CYCLE_SCOPES) {
        if (other === scope) continue;
        if (scopes[other]?.has(cycleId)) return PROJECT_CYCLE_SCOPE_LABELS[other];
      }
    }
    // In the candidate list, in no scope we can name. Say nothing rather than
    // inventing a reason for a shape that should not arrive.
    return null;
  }
  // Not among the candidates. Only an evidently-loaded list makes its absence
  // evidence of anything; see `withStoredOption`, which draws the same line.
  return candidateCount > 0 ? 'No longer offered' : null;
}
